'use client';

import React, { useMemo, useState } from 'react';
import { Card, Button } from '@ui';
import { DEFAULT_FILTERS, type FilterItem } from './FiltersData';

export type { FilterItem } from './FiltersData';

export type Result = {
  id: number;
  title: string;
};

type FiltersProps = {
  id?: string;
  className?: string;
  filters?: FilterItem[];
  // controlled selected ids
  selected?: string[];
  onSelectedChange?: (ids: string[]) => void;
};

export default function Filters({
  id,
  className = '',
  filters = DEFAULT_FILTERS,
  selected: controlledSelected,
  onSelectedChange,
}: FiltersProps) {
  const [internalSelected, setInternalSelected] = useState<Record<string, boolean>>({});
  const selectedRecord = controlledSelected
    ? controlledSelected.reduce(
        (acc, cur) => ({ ...acc, [cur]: true }),
        {} as Record<string, boolean>
      )
    : internalSelected;

  const selectedCount = useMemo(
    () => Object.values(selectedRecord).filter(Boolean).length,
    [selectedRecord]
  );

  function toggle(idKey: string) {
    const nextRec = { ...selectedRecord, [idKey]: !selectedRecord[idKey] };
    if (controlledSelected) {
      const ids = Object.keys(nextRec).filter((k) => nextRec[k]);
      onSelectedChange?.(ids);
    } else {
      setInternalSelected(nextRec);
    }
  }

  function clearAll() {
    if (controlledSelected) onSelectedChange?.([]);
    else setInternalSelected({});
  }

  const results: Result[] = useMemo(() => {
    const active = Object.keys(selectedRecord).filter((k) => selectedRecord[k]);
    if (active.length === 0)
      return Array.from({ length: 8 }, (_, i) => ({
        id: i + 1,
        title: `Result ${i + 1}`,
      }));
    return Array.from({ length: active.length * 2 }, (_, i) => ({
      id: i + 1,
      title: `${active.join(',')}-${i + 1}`,
    }));
  }, [selectedRecord]);

  return (
    <Card className={`${className} filters-card`.trim()} id={id ?? 'filtersCard'}>
      <div className='row filters-chips'>
        <div className='row filters-chips-row' id='filterChips' role='group' aria-label='Filters'>
          {filters.map((f) => (
            <Button
              key={f.id}
              className={`chip-toggle${selectedRecord[f.id] ? ' is-on' : ''}`}
              onClick={() => toggle(f.id)}
              type='button'
              label={
                <>
                  {f.label}
                  <span className='count' style={{ marginLeft: 6 }}>
                    {f.count}
                  </span>
                </>
              }
            />
          ))}
        </div>

        <div className='row'>
          <span className='hint' id='filtersSummary'>
            {selectedCount} selected
          </span>
          <Button
            className='btn-ghost btn-sm'
            id='filtersClear'
            onClick={clearAll}
            type='button'
            label='Clear all'
          />
        </div>
      </div>

      <div className='grid-3 mt-16' id='filtersResults'>
        {results.map((r) => (
          <Card key={r.id} className='metric'>
            <h3 className='metric-title'>{r.title}</h3>
            <div className='metric-meta'>Sample result</div>
          </Card>
        ))}
      </div>
    </Card>
  );
}
